import { HomeView } from '@/views/Home/Home.view';
import { withPreRenderSettings } from '@/utils/hocs';
import { getSeoFullData } from '@/api/helpers/seo.helpers';
import {
  GetAdsResponse,
  GetPopularRequestsResponse,
  ServiceName,
} from '@/api/types/ads.types';
import { ServerDataProvider } from '@/contexts';
import { apolloClient } from '@/api/apolloClient';
import { GET_ADS, GET_POPULAR_REQUESTS } from '@/api/queries/ads.queries';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { PlatformSource } from '@/api/types/common.types';
import { getPlatformSource } from '@/utils/getPlatformSource';
import { GET_ROOT_CATEGORIES } from '@/api/queries/categories.queries';
import { GetRootCategoriesResponse } from '@/api/types/categories.types';

function Home({
  initialPremiumAds,
  popularRequests,
  rootCategories,
}: {
  initialPremiumAds: GetAdsResponse | null;
  popularRequests: GetPopularRequestsResponse | null;
  rootCategories: GetRootCategoriesResponse | null;
}) {
  return (
    <ServerDataProvider
      premiumAds={initialPremiumAds}
      popularRequests={popularRequests}
      rootCategories={rootCategories}
    >
      <HomeView />
    </ServerDataProvider>
  );
}

Home.getInitialProps = withPreRenderSettings(
  async (context, client, deviceType) => {
    const isBrowser = typeof window !== 'undefined';

    const seoFullData = await getSeoFullData(client, context);
    const userAgent = context.req?.headers['user-agent'] || '';
    const isSSRWebView = userAgent.indexOf('miniApp') >= 0;
    const platformSource = getPlatformSource(deviceType);

    if (!isBrowser) {
      const [initialPremiumAds, popularRequests, rootCategories] =
        await Promise.all([
          apolloClient<GetAdsResponse>(
            client,
            {
              query: GET_ADS(ServiceName.Premium),
              variables: {
                adKind: ServiceName.Premium,
                first: AdsChunkSize.Middle,
                source: isSSRWebView ? PlatformSource.App : platformSource,
              },
            },
            context
          ),
          apolloClient<GetPopularRequestsResponse>(
            client,
            {
              query: GET_POPULAR_REQUESTS,
              variables: {
                path: context.pathname,
              },
            },
            context
          ),
          apolloClient<GetRootCategoriesResponse>(
            client,
            { query: GET_ROOT_CATEGORIES },
            context
          ),
        ]);

      return {
        initialPremiumAds: initialPremiumAds?.data ?? null,
        popularRequests: popularRequests?.data ?? null,
        rootCategories: rootCategories?.data ?? null,
        seoFullData: seoFullData ?? null,
      };
    }

    return { seoFullData: seoFullData ?? null };
  }
);

export default Home;
